import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSalesmanList } from "../../redux/actions/salesmanAction"; 
import { DashboardLayout } from "../../Pages/dashboard/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaShare, FaUser, FaArrowLeft } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import SearchBar from "../../Pages/Components/SearchBar";
import SortableTable from "../../Pages/Components/SortableTable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";
import dayjs from "dayjs";
import { FiX } from "react-icons/fi";

const SalesmanList = () => {
  const dispatch = useDispatch();
  const { loading, salesmen, error } = useSelector((state) => state.salesman);
  const { userInfo } = useSelector((state) => state.auth);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const navigate = useNavigate();

  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(fetchSalesmanList(userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token]);

  // Safely format error for display (avoid rendering raw objects)
  if (error) {
    const errorMessage =
      typeof error === "object" && error !== null
        ? error.message || error.status || JSON.stringify(error)
        : error;

    return (
      <DashboardLayout>
        <div className="mt-2 max-w-full mx-auto rounded-lg p-4">
          <p className="text-red-600 font-bold text-center">
            Error: {errorMessage}
          </p>
        </div>
      </DashboardLayout>
    );
  }
  console.log("salesmen:", salesmen);

  // Convert single salesman object to array if needed
  const salesmanArray = Array.isArray(salesmen)
    ? salesmen
    : salesmen
      ? [salesmen]
      : [];

  const filteredSalesmen = salesmanArray
    .filter((salesman) => {
      const search = searchValue.toLowerCase();
      const name = salesman.name?.toLowerCase() || "";
      const mobile = salesman.mobile?.toLowerCase() || "";
      const email = salesman.email?.toLowerCase() || "";
      const city = salesman.city?.toLowerCase() || "";
      const state = salesman.state?.toLowerCase() || "";
      const pincode = salesman.pincode?.toLowerCase() || "";
      const status = salesman.is_active ? "active" : "inactive";
      return (
        name.includes(search) ||
        mobile.includes(search) ||
        email.includes(search) ||
        city.includes(search) ||
        state.includes(search) ||
        pincode.includes(search) ||
        status === search
      );
    })
    .filter((salesman) => {
      if (!startDate && !endDate) return true;

      const rawDate = salesman.created_at?.trim();

      // Parse DD-MM-YYYY manually to ISO (YYYY-MM-DD)
      const [day, month, year] = rawDate.split("-");
      if (!day || !month || !year) {
        console.warn(
          "Malformed date string:",
          rawDate,
          "for salesman ID:",
          salesman.user_id
        );
        return false;
      }

      const formattedDate = `${year}-${month}-${day}`;
      const createdDate = dayjs(formattedDate);

      if (!createdDate.isValid()) {
        console.warn(
          "Parsed invalid date:",
          formattedDate,
          "for salesman ID:",
          salesman.user_id
        );
        return false;
      }

      const start = startDate ? dayjs(startDate).startOf("day") : null;
      const end = endDate ? dayjs(endDate).endOf("day") : null;

      if (start && createdDate.isBefore(start)) return false;
      if (end && createdDate.isAfter(end)) return false;

      return true;
    });

  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

const handleDownload = async () => {
  try {
    const response = await dispatch(
      fetchSalesmanList(userInfo.access_token, { download: true })
    );
    
    console.log("Download response:", response);
    
    const fileUrl = response?.payload?.download_url;
    
    if (fileUrl) {
      downloadFile(fileUrl);
    } else {
      alert("No download URL received from server");
      console.warn("No download_url in response:", response);
    }
  } catch (err) {
    console.error("Download failed:", err);
    alert("Failed to initiate download");
  }
};

  const columns = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Mobile", key: "mobile" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Pincode", key: "pincode" },
    { label: "Created At", key: "created_at" },
    { label: "Actions", key: "actions", sortable: false },
  ];

  const renderRow = (salesman) => (
    <tr
      key={salesman.user_id}
      className="hover:bg-blue-50 hover:text-gray-950 hover:font-semibold"
    >
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.name || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.email || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.mobile || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.city || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.state || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.pincode || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400 text-sm">{salesman.created_at || "-"}</td>
      <td className="px-2 py-4 border-b border-gray-400">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            title="Edit"
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-xs"
            onClick={() => {
              navigate("/dashboard/createsalesman", {
                state: { salesmanData: salesman, editMode: true },
              });
            }}
          >
            <FaEdit />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <DashboardLayout>
      <div className="mt-2 max-w-full mx-auto rounded-lg p-2">
        <div className="flex items-center justify-between ">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600 ">
              Salesmen List
            </h2>
          </div>

          <div className="flex space-x-2">
            <button
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 flex items-center px-4 py-2 text-sm font-medium text-white border border-amber-600 rounded-lg transition shadow-md hover:shadow-lg"

              onClick={() => {
                navigate("/dashboard/createsalesman");
              }}
            >
              <FaUser className="mr-2" /> Add Salesman
            </button>
            <button
             onClick={handleDownload}
              className="bg-white flex items-center px-4 py-2 text-sm font-medium text-black border border-gray-500 rounded-md transition"          >
              <FiDownload className="w-4 h-4 mr-2 text-amber-600" />
              Export
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center mb-4 justify-between gap-4">
          <div className="w-2/3 flex flex-wrap gap-4 items-center">
            <SearchBar
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search by Name / Email / Mobile"
            />

            <div className="relative w-1/3">
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                isClearable
                placeholderText="Select date range"
                dateFormat="dd MMM yyyy"
                className="placeholder-gray-500 w-full px-4 py-2 border border-gray-500 rounded-md text-sm "
              />
            </div>
          </div>

          <div></div>
        </div>

        {filteredSalesmen.length === 0 ? (
          <p className="font-bold text-md text-center text-gray-950 py-6">
            No salesmen found
          </p>
        ) : (
          <SortableTable
            columns={columns}
            data={filteredSalesmen}
            renderRow={renderRow}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default SalesmanList;
